import type { History } from 'history';
import qs from 'query-string';
import { useMemo } from 'react';
import type { match as matchType } from 'react-router-dom';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch
} from 'react-router-dom';

type TRouter<TParams> = {
  match: matchType<{ [K in keyof TParams]?: string }>;
  location: History['location'];
  query: TParams;
  pathname: string;
  push: History['push'];
  replace: History['replace'];
  goBack: History['goBack'];
};

const useRouter = <TParams extends { [K in keyof TParams]?: TParams[K] }>(
  initialPath: string | string[] = ''
): TRouter<TParams> => {
  const params = useParams<{ [K in keyof TParams]?: string }>() as TParams;
  const location = useLocation();
  const match = useRouteMatch(initialPath) as matchType<{
    [K in keyof TParams]?: string;
  }>;
  const { replace, push, goBack } = useHistory();

  return useMemo(() => {
    return {
      push,
      replace,
      goBack,
      pathname: location.pathname,
      query: {
        ...qs.parse(location.search), // Convert string to object
        ...params
      },
      location,
      match
    };
  }, [push, replace, goBack, location, params, match]);
};

export default useRouter;
